import React from 'react';
import styled, { css } from 'styled-components';
import Picture from '@westwing/ui-kit/Picture';
import { InlineSpinner } from '@westwing/ui-kit/Spinner';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { IDSContentfulImageFile } from '@ids/types/contentful';
import { makeSourcesSet } from '@ids/components/Media/MediaImage';

const Image = styled(Picture)<{ zoomed?: boolean }>`
    margin: auto;
    max-width: 300%;
    min-width: 100%;
    object-fit: contain;
    width: 100%;
`;

const ImageWrapper = styled.div<{ $zoomOut: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;

    ${p =>
        p.$zoomOut
            ? css`
                  cursor: zoom-out;
              `
            : css`
                  cursor: zoom-in;
              `}
`;

export type ZoomImageProps = {
    image: IDSContentfulImageFile;
    onError: () => void;
    altTag: string;
};

export const ZoomImage = ({ image, altTag, onError }: ZoomImageProps) => {
    const src = image.file.url;
    const { webpSrcSet, jpegSrcSet } = makeSourcesSet(70, src);
    const [zoomDirection, setZoomDirection] = React.useState<'in' | 'out'>('in');

    const onClick =
        ({ zoomIn, zoomOut, state }: ReactZoomPanPinchRef) =>
        () => {
            if (zoomDirection === 'out') {
                zoomOut();
            } else {
                zoomIn();
            }

            if (state.scale > 2.5) {
                setZoomDirection('out');
            }
            if (state.scale < 1.5) {
                setZoomDirection('in');
            }
        };

    const [isImageLoading, setIsImageLoading] = React.useState(true);

    return (
        <TransformWrapper maxScale={3}>
            {zoomRef => (
                <TransformComponent contentStyle={{ width: '100%' }} wrapperStyle={{ width: '100%' }}>
                    {isImageLoading && <InlineSpinner style={{ position: 'relative' }} />}
                    <ImageWrapper onClick={onClick(zoomRef)} $zoomOut={zoomDirection === 'out'}>
                        <Image
                            fallback={{
                                alt: altTag,
                                url: src,
                            }}
                            sources={
                                <React.Fragment>
                                    <source type="image/webp" srcSet={webpSrcSet} sizes="100vw" />
                                    <source type="image/jpeg" srcSet={jpegSrcSet} sizes="100vw" />
                                </React.Fragment>
                            }
                            errorFunction={onError}
                            loadFunction={() => setIsImageLoading(false)}
                        />
                    </ImageWrapper>
                </TransformComponent>
            )}
        </TransformWrapper>
    );
};
